import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../Config/Firebase/config";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../Cart/cartSlice";
import { useUserAuth } from "../../context/userAuthContext";
import iconData from "./icon";
import Loading from "../../Utility/loading";
import "./productDetails.css";

const ProductDetails = () => {
  const { category, productId } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, googleSignIn } = useUserAuth();

  const cartItems = useSelector((state) => state.cart.items);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // ======================================================
  // FETCH PRODUCT
  // ======================================================

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);

      try {
        const productRef = doc(db, "product", productId);
        const productSnapshot = await getDoc(productRef);

        if (!productSnapshot.exists()) {
          toast.error("Product not found");
          setProduct(null);
          return;
        }

        const productData = {
          id: productSnapshot.id,
          ...productSnapshot.data(),
        };

        // Optional category validation
        if (productData.category !== category) {
          toast.error("Product category does not match");
          setProduct(null);
          return;
        }

        setProduct(productData);
      } catch (error) {
        console.error("Error loading product:", error);
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId, category]);

  // ======================================================
  // ADD TO CART
  // ======================================================

  const handleAddToCart = async () => {
    if (!product) return;

    if (!user) {
      try {
        const signedInUser = await googleSignIn();

        if (signedInUser) {
          dispatch(
            addToCart({
              product,
              userId: signedInUser.uid,
            }),
          );

          toast.success(`${product.title} added to cart`);
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to sign in");
      }
    } else {
      dispatch(
        addToCart({
          product,
          userId: user.uid,
        }),
      );

      toast.success(`${product.title} added to cart`);
    }
  };

  // ======================================================
  // CART CHECK
  // ======================================================

  const isInCart = () => {
    if (!product) return false;

    return cartItems.some((item) => item.id === product.id);
  };

  // ======================================================
  // SHARE PRODUCT
  // ======================================================

  const handleShare = async () => {
    if (!product) return;

    const shareUrl = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({
          title: product.title,
          text: `Check out ${product.title}`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Product link copied!");
      }
    } catch (error) {
      // User cancelled the native share dialog.
      if (error.name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success("Product link copied!");
        } catch (copyError) {
          toast.error("Could not share product");
        }
      }
    }
  };

  // ======================================================
  // LOADING
  // ======================================================

  if (loading) {
    return <Loading />;
  }

  // ======================================================
  // PRODUCT NOT FOUND
  // ======================================================

  if (!product) {
    return (
      <section className="product-details section">
        <div className="container">
          <div className="product-not-found">
            <h2>Product Not Found</h2>

            <button className="btn" onClick={() => navigate("/")}>
              Go Home
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ======================================================
  // PRODUCT DETAILS
  // ======================================================

  return (
    <section className="product-details section">
      <div className="container">
        {/* Breadcrumb */}
        <div className="product-breadcrumb">
          <span onClick={() => navigate("/")}>Home</span>

          <span>/</span>

          <span
            onClick={() =>
              navigate(`/category/${encodeURIComponent(category)}`)
            }
          >
            {category}
          </span>

          <span>/</span>

          <span>{product.title}</span>
        </div>

        <div className="product-details-wrapper">
          {/* Product Image */}
          <div className="product-details-image">
            <img src={product.image} alt={product.title} />
          </div>

          {/* Product Information */}
          <div className="product-details-info">
            <h1>{product.title}</h1>

            <p className="product-details-category">
              Category: {product.category}
            </p>

            <p className="product-details-price">Rs {product.price}</p>

            <div className="product-details-content">
              <h3>Product Details</h3>

              <p>{product.details || "No product details available."}</p>
            </div>

            {/* Available */}
            {product.available !== undefined && (
              <p className="product-availability">
                Status:{" "}
                <strong>
                  {product.available ? "Available" : "Out of Stock"}
                </strong>
              </p>
            )}

            <div className="product-details-actions">
              {isInCart() ? (
                <button className="view-cart" onClick={() => navigate("/cart")}>
                  <span>View in Cart</span>
                </button>
              ) : (
                <button
                  className="add-to-cart"
                  onClick={handleAddToCart}
                  disabled={product.available === false}
                >
                  Add To Cart
                </button>
              )}

              <button className="share-product" onClick={handleShare}>
                Share Product
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDetails;
